import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar";

export function AppShell({
  title,
  centered = false,
  children,
  width = "full",
  viewport = false,
}: {
  title: string;
  centered?: boolean;
  width?: "full" | "reading" | "form";
  viewport?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "bg-background",
        viewport ? "flex h-svh flex-col overflow-hidden" : "min-h-svh",
      )}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[110] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>
      <Sidebar />
      <div
        className={cn(
          "flex min-w-0 flex-col sm:pl-(--sidebar-width) sm:transition-[padding] sm:duration-200",
          viewport ? "min-h-0 flex-1" : "min-h-svh",
        )}
      >
        <div aria-hidden className="h-14 shrink-0 sm:hidden" />
        <main
          id="main-content"
          aria-label={title}
          tabIndex={-1}
          className={cn(
            "flex min-w-0 flex-1 flex-col gap-8 px-4 pb-8 pt-6 outline-none sm:px-10 sm:pb-10 sm:pt-8",
            centered && "items-center justify-center py-12",
            width === "reading" && "w-full max-w-5xl self-center",
            width === "form" && "w-full max-w-[47.5rem] self-center",
            viewport && "min-h-0 gap-4 overflow-hidden pb-4 pt-2 sm:px-6 sm:pb-4 sm:pt-4",
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && (
          <p className="max-w-2xl break-words text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-sm flex-col items-center gap-3 px-4 py-14 text-center">
      <div aria-hidden className="mb-1 text-muted-foreground [&>svg]:size-7">
        {icon}
      </div>
      <h2 className="text-sm font-semibold">{title}</h2>
      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
      {children && <div className="mt-2 flex flex-wrap justify-center gap-2">{children}</div>}
    </div>
  );
}
